export interface GeoPosition {
  lat: number
  lng: number
  accuracy: number
}

export type GeofenceStatus = "inside" | "outside" | "unknown"

const EARTH_RADIUS_M = 6_371_000

/** Haversine distance between two lat/lng points in meters. */
export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const sinLat = Math.sin(dLat / 2)
  const sinLng = Math.sin(dLng / 2)
  const h =
    sinLat * sinLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180
}

/** Check if a position is within a property's geofence. */
export function checkGeofence(
  position: GeoPosition,
  property: { latitude?: number | null; longitude?: number | null; geofence_radius_meters?: number | null }
): GeofenceStatus {
  if (property.latitude == null || property.longitude == null) return "unknown"
  const radius = property.geofence_radius_meters ?? 150
  const dist = distanceMeters(position, { lat: property.latitude, lng: property.longitude })
  return dist <= radius ? "inside" : "outside"
}

/** Promisified wrapper around navigator.geolocation.getCurrentPosition. */
export function capturePosition(options?: PositionOptions): Promise<GeoPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000, ...options }
    )
  })
}
